import { useLocalStorage } from '@texas/hooks/useLocalStorage';
import { WidgetLayout, WidgetOption } from './useWidgetLayout';
import { randomSeed } from '@texas/resources/constants';
import { useCallback, useState } from 'react';
import {
  widgetSizeConstraints,
  defaultWidgetLayouts,
} from './defaultWidgetLayouts';
import { WidgetOptions, WidgetType } from './shared/types';

interface Props {
  defaultLayout: WidgetLayout;
  localStorageKey?: string;
  widgetOptions: WidgetOption;
}

function isValidWidgetLayout(value: unknown): value is WidgetLayout {
  return (
    typeof value === 'object' &&
    value !== null &&
    'widgets' in value &&
    Array.isArray((value as { widgets: unknown }).widgets) &&
    'layout' in value &&
    typeof (value as { layout: unknown }).layout === 'object' &&
    (value as { layout: unknown }).layout !== null
  );
}

export function useWidgetFactory({ defaultLayout, localStorageKey }: Props) {
  const [recentlyAddedWidgetId, setRecentlyAddedWidgetId] = useState<
    string | null
  >(null);
  const [layout, setLayout] = useLocalStorage(
    localStorageKey,
    defaultLayout,
    undefined,
    isValidWidgetLayout,
  );

  const addNewWidget = useCallback(
    (
      widget: WidgetType,
      name: string,
      color: string | null,
      options?: WidgetOptions,
    ) => {
      const widgetId = Math.floor(Math.random() * randomSeed).toString();
      const constraints = widgetSizeConstraints[widget];

      setLayout((l) => ({
        layout: Object.fromEntries(
          Object.entries(l.layout).map(([breakpoint, items]) => {
            return [
              breakpoint,
              [
                ...items,
                {
                  ...defaultWidgetLayouts[widget].lg,
                  ...constraints,
                  i: widgetId,
                  y: items.reduce(
                    (bottom, item) => Math.max(bottom, item.y + item.h),
                    0,
                  ),
                },
              ],
            ];
          }),
        ),
        widgets: [
          ...l.widgets,
          {
            id: widgetId,
            type: widget,
            layoutIndex: widgetId,
            name: name,
            categoryColor: color,
            widgetOptions: options,
          },
        ],
        currentLayout: l.currentLayout,
      }));
      setRecentlyAddedWidgetId(widgetId);
    },
    [setLayout],
  );

  const clearRecentlyAddedWidget = useCallback((widgetId: string) => {
    setRecentlyAddedWidgetId((currentWidgetId) =>
      currentWidgetId === widgetId ? null : currentWidgetId,
    );
  }, []);

  function removeWidget(id: string) {
    setLayout((l) => ({
      ...l,
      widgets: [...l.widgets.filter((x) => x.id !== id)],
    }));
  }

  function updateWidget(
    id: string,
    name: string,
    color: string | null,
    options?: WidgetOptions,
  ) {
    setLayout((l) => ({
      ...l,
      widgets: [
        ...l.widgets.map((x) => {
          if (x.id === id) {
            return { ...x, name, categoryColor: color, widgetOptions: options };
          }

          return x;
        }),
      ],
    }));
  }

  return {
    addNewWidget,
    removeWidget,
    updateWidget,
    recentlyAddedWidgetId,
    clearRecentlyAddedWidget,
    layout,
    setLayout,
  };
}
